import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import { CustomButton } from "../../components/CustomButton";
import { useLayout } from "../../context/LayoutContext";
import "./notifications.css";
import {
  API_GET_NOTIFICATIONS,
  API_UPDATE_NOTIFICATION,
} from "../../api/notification.service";

const typeOptions = [
  { label: "Info", value: "info" },
  { label: "Warning", value: "warning" },
  { label: "Success", value: "success" },
  { label: "Error", value: "error" },
  { label: "Secondary", value: "secondary" },
  { label: "Contrast", value: "contrast" },
];

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toastMessage } = useLayout();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await API_GET_NOTIFICATIONS();
      setNotifications(data.rows || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toastMessage({ severity: "error", detail: "Unable to load notifications." });
    } finally {
      setLoading(false);
    }
  }, [toastMessage]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const metrics = useMemo(() => {
    const enabled = notifications.filter((notification) => notification.isEnabled).length;
    const disabled = notifications.length - enabled;
    return {
      total: notifications.length,
      enabled,
      disabled,
    };
  }, [notifications]);

  const toggleNotification = async (notification) => {
    try {
      await API_UPDATE_NOTIFICATION(notification.id, {
        isEnabled: !notification.isEnabled,
      });
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notification.id
            ? { ...notif, isEnabled: !notif.isEnabled }
            : notif
        )
      );
      toastMessage({ severity: "success", detail: "Notification status updated." });
    } catch (error) {
      console.error("Error updating notification status:", error);
      toastMessage({ severity: "error", detail: "Unable to update notification status." });
    }
  };

  const deleteNotification = async (notification) => {
    try {
      await API_UPDATE_NOTIFICATION(notification.id, {
        isActive: false,
      });
      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== notification.id)
      );
      toastMessage({ severity: "success", detail: "Notification archived." });
    } catch (error) {
      console.error("Error deleting notification:", error);
      toastMessage({ severity: "error", detail: "Unable to archive notification." });
    }
  };

  const editNotification = (notification) => {
    setSelectedNotification(notification);
    setDialogVisible(true);
  };

  const renderType = (rowData) => (
    <span className={`status-pill ${rowData.type === "error" ? "danger" : rowData.type === "warning" ? "warning" : rowData.type === "success" ? "success" : "info"}`}>
      {rowData.type}
    </span>
  );

  const renderEnabled = (rowData) => (
    <div className="notification-switch">
      <InputSwitch
        checked={rowData.isEnabled}
        onChange={() => toggleNotification(rowData)}
      />
      <span>{rowData.isEnabled ? "Enabled" : "Disabled"}</span>
    </div>
  );

  return (
    <div className="page-shell notification-page">
      {loading ? (
        <div className="loading-container">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </div>
      ) : null}

      <div className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">Notification governance</h1>
        </div>
      </div>

      <div className="metric-grid">
        <div className="metric-card">
          <span className="metric-card__label">Total Notifications</span>
          <div className="metric-card__value">{metrics.total}</div>
        </div>
        <div className="metric-card">
          <span className="metric-card__label">Enabled</span>
          <div className="metric-card__value">{metrics.enabled}</div>
        </div>
        <div className="metric-card">
          <span className="metric-card__label">Disabled</span>
          <div className="metric-card__value">{metrics.disabled}</div>
        </div>
      </div>

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">Notification manager</h2>
          </div>
        </div>

        <DataTable value={notifications} paginator rows={8} className="notification-table">
          <Column field="companyId" header="Company ID" sortable />
          <Column field="message" header="Message" />
          <Column field="type" header="Type" body={renderType} />
          <Column field="isEnabled" header="Enabled" body={renderEnabled} />
          <Column
            body={(rowData) => (
              <div className="notification-actions">
                <Button
                  icon="pi pi-pencil"
                  className="p-button-rounded p-button-outlined"
                  onClick={() => editNotification(rowData)}
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-danger p-button-text"
                  onClick={() => deleteNotification(rowData)}
                />
              </div>
            )}
            header="Actions"
          />
        </DataTable>
      </div>

      <Dialog
        visible={dialogVisible}
        header="Edit Notification"
        onHide={() => setDialogVisible(false)}
        modal
        style={{ width: "min(520px, 92vw)" }}
      >
        <div className="notification-dialog">
          <div className="company-dialog-field">
            <label>Message</label>
            <InputText
              value={selectedNotification?.message || ""}
              onChange={(e) =>
                setSelectedNotification({
                  ...selectedNotification,
                  message: e.target.value,
                })
              }
            />
          </div>
          <div className="company-dialog-field">
            <label>Type</label>
            <Dropdown
              value={selectedNotification?.type || "info"}
              options={typeOptions}
              onChange={(e) =>
                setSelectedNotification((prev) => ({
                  ...prev,
                  type: e.value,
                }))
              }
            />
          </div>
          <div className="notification-checkbox">
            <Checkbox
              inputId="isEnabled"
              checked={selectedNotification?.isEnabled || false}
              onChange={(e) =>
                setSelectedNotification({
                  ...selectedNotification,
                  isEnabled: e.checked,
                })
              }
            />
            <label htmlFor="isEnabled">Enabled</label>
          </div>
        </div>

        <div className="company-dialog-actions">
          <CustomButton
            label="Cancel"
            varient="outline"
            onClick={() => setDialogVisible(false)}
          />
          <CustomButton
            label="Update"
            onClick={async () => {
              if (selectedNotification) {
                try {
                  await API_UPDATE_NOTIFICATION(
                    selectedNotification.id,
                    selectedNotification
                  );
                  setNotifications((prev) =>
                    prev.map((notif) =>
                      notif.id === selectedNotification.id
                        ? selectedNotification
                        : notif
                    )
                  );
                  setDialogVisible(false);
                  toastMessage({
                    severity: "success",
                    detail: "Notification updated successfully.",
                  });
                } catch (error) {
                  console.error("Error updating notification:", error);
                  toastMessage({
                    severity: "error",
                    detail: "Unable to update notification.",
                  });
                }
              }
            }}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default Notifications;
